'use client';

import { useState } from 'react';
import Icon from '@/components/ui/AppIcon';

interface FAQ {
  id: string;
  question: string;
  answer: string;
}

const faqs: FAQ[] = [
  {
    id: 'faq_trial',
    question: 'How does the trial work?',
    answer:
      'Start with a 14-day trial for just $1 on any plan. Payment method required upfront. Access all features during the trial period. Cancel anytime before the trial ends to avoid being charged the full subscription price.',
  },
  {
    id: 'faq_billing',
    question: 'How does billing work?',
    answer:
      'All plans are billed monthly. You can upgrade, downgrade, or cancel anytime. If you upgrade mid-month, we prorate the difference. Invoices are sent via email on your billing date.',
  },
  {
    id: 'faq_cancel',
    question: 'Can I cancel anytime?',
    answer:
      'Yes! Cancel your subscription anytime from your account settings. Your access continues until the end of your current billing period. No cancellation fees or penalties.',
  },
  {
    id: 'faq_refund',
    question: 'What is your refund policy?',
    answer:
      'We offer a 30-day money-back guarantee on all plans. If you\'re not satisfied for any reason, contact us within 30 days of your first payment for a full refund.',
  },
  {
    id: 'faq_upgrade',
    question: 'Can I upgrade or downgrade my plan?',
    answer:
      'Absolutely! Upgrade anytime to access more features or increase your quote limit. Downgrade at the end of your billing cycle. We prorate upgrades so you only pay the difference.',
  },
  {
    id: 'faq_quotes',
    question: 'What happens if I exceed my quote limit?',
    answer:
      'If you reach your monthly quote limit, you\'ll receive a notification. You can upgrade to a higher plan instantly, or purchase additional quotes at $1 per quote. Enterprise plans have unlimited quotes.',
  },
  {
    id: 'faq_payment',
    question: 'What payment methods do you accept?',
    answer:
      'We accept all major credit cards (Visa, Mastercard, American Express, Discover) and ACH bank transfers for Enterprise plans. All payments are processed securely through Stripe.',
  },
  {
    id: 'faq_data',
    question: 'Is my data secure?',
    answer:
      'Yes! We use 256-bit SSL encryption for all data transmission. Your information is stored on secure servers with daily backups. We\'re SOC 2 certified and GDPR compliant.',
  },
];

export default function PricingFAQ() {
  const [openIndex, setOpenIndex] = useState<number | null>(null);

  const toggleFAQ = (index: number) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  return (
    <section className="section-padding bg-white border-y border-border">
      <div className="container-custom">
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-heading font-bold text-foreground mb-4">
            <span className="text-primary">Pricing</span> Questions Answered
          </h2>
          <p className="text-xl text-muted-foreground">
            Everything you need to know about our pricing and billing
          </p>
        </div>

        <div className="max-w-3xl mx-auto space-y-4">
          {faqs.map((faq, index) => (
            <div
              key={faq.id}
              className="bg-card border border-border rounded-xl overflow-hidden hover:border-primary/30 transition-colors"
            >
              <button
                onClick={() => toggleFAQ(index)}
                className="w-full flex items-center justify-between p-6 text-left group"
              >
                <span className="text-lg font-heading font-semibold text-foreground group-hover:text-primary transition-colors pr-4">
                  {faq.question}
                </span>
                <Icon
                  name="ChevronDownIcon"
                  size={24}
                  variant="outline"
                  className={`text-muted-foreground flex-shrink-0 transition-transform duration-300 ${
                    openIndex === index ? 'rotate-180' : ''
                  }`}
                />
              </button>

              <div
                className={`grid transition-all duration-300 ease-out ${
                  openIndex === index
                    ? 'grid-rows-[1fr] opacity-100'
                    : 'grid-rows-[0fr] opacity-0'
                }`}
              >
                <div className="overflow-hidden">
                  <div className="px-6 pb-6 pt-0">
                    <p className="text-muted-foreground leading-relaxed">
                      {faq.answer}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="text-center mt-12">
          <p className="text-muted-foreground mb-4">Still have questions?</p>
          <a
            href="/homepage#contact"
            className="inline-flex items-center gap-2 text-primary font-semibold hover:gap-3 transition-all"
          >
            <span>Contact our sales team</span>
            <Icon name="ArrowRightIcon" size={20} variant="outline" />
          </a>
        </div>
      </div>
    </section>
  );
}